import React, { useState } from 'react';
import { Stack } from '@mui/material';
import {
  ModalFooterButtons,
  StyledModal,
  SingleDateTimePicker
} from '@/components/NewUI/Components';
import { classes } from './styles';
import { classes as importClasses } from '../EditJob/styles';
import { useMutation } from '@tanstack/react-query';
import Api from './API';
import Dayjs from 'dayjs';
import { userTimeZone } from '../utils/formatToUserTimeZone';
import { IJob } from '../Jobs/types';

export default function UpdateExpiryDateAndPublishJobModal({
  open,
  onClose,
  setSnackbar,
  jobId,
  refetch,
  getNewStateAndStatus,
  jobTimezone
}: {
  open: boolean;
  onClose: () => void;
  setSnackbar: (snackbar: { message: string; state: 'success' | 'error' }) => void;
  jobId: number;
  refetch?: () => void;
  getNewStateAndStatus?: (data: IJob, jobId: number) => void;
  jobTimezone?: string;
}) {
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [expiryDateError, setExpiryDateError] = useState<string>('');
  const validateExpiryDate = () => {
    if (!expiryDate) setExpiryDateError('Expiry date is required');
    else if (expiryDate < new Date()) setExpiryDateError('Expiry date must be in the future');
    else setExpiryDateError('');
    return !expiryDateError;
  };

  const { mutate: updateExpiryDateAndPublish, isLoading: updatingExpiryDateAndPublish } =
    useMutation({
      mutationFn: async () =>
        await Api.updateExpiryDateAndPublish(jobId, Dayjs(expiryDate).toISOString()),
      onSuccess: (data) => {
        setSnackbar({
          message: 'Job published with expiry date',
          state: 'success'
        });
        handleClose();
        refetch && refetch();
        getNewStateAndStatus && getNewStateAndStatus(data, jobId);
      },
      onError: (error: { errors: string }) => {
        setSnackbar({
          message: error.errors,
          state: 'error'
        });
      }
    });

  const handleClose = () => {
    setExpiryDate(null);
    setExpiryDateError('');
    onClose();
  };

  return (
    <StyledModal
      isOpen={open}
      handleClose={handleClose}
      label="set-expiry-date-modal"
      styleOverrides={{ ...classes.modalStyleOverrides, height: '325px', width: '600px' }}
    >
      <Stack sx={classes.modalContainer}>
        <Stack sx={{ ...classes.modalHeader, margin: 'unset' }}>Set expiry date</Stack>
        <Stack sx={{ paddingTop: 2, flexGrow: 1 }}>
          <SingleDateTimePicker
            label="Expiry date"
            inputStyles={{ ...importClasses.datePicker, width: '60%' }}
            onChange={(value) => {
              value && setExpiryDateError('');
              setExpiryDate(value);
            }}
            timezone={jobTimezone || userTimeZone}
            value={expiryDate}
            disablePast
            error={expiryDateError}
          />
        </Stack>
        <ModalFooterButtons
          primaryButtonText="Save"
          secondaryButtonText="Cancel"
          secondaryButtonCallback={handleClose}
          primaryButtonMinWidth="95px"
          primaryButtonCallback={() => validateExpiryDate() && updateExpiryDateAndPublish()}
          primaryButtonID={`set-expiry-date-modal-save-button`}
          secondaryButtonID={`set-expiry-date-modal-cancel-button`}
          isLoading={updatingExpiryDateAndPublish}
        />
      </Stack>
    </StyledModal>
  );
}
